import { useMemo } from 'react';
import { TableId, useCustomTableView } from './userPreferences';

type GridType =
  | 'DocumentListItem'
  | 'DocumentList'
  | 'TemplateListItem'
  | 'TemplateList';

type GridTypeColumnMap = {
  DocumentListItem: DocumentColumns;
  DocumentList: DocumentColumns;
  TemplateListItem: TemplateColumns;
  TemplateList: TemplateColumns;
};

function getGridColumns<T extends GridType>(
  gridType: T,
): Column<GridTypeColumnMap[T]>[] {
  switch (gridType) {
    case 'DocumentList':
      return DOCUMENT_LIST_COLUMNS as Column<GridTypeColumnMap[T]>[];
    case 'DocumentListItem':
      return DOCUMENT_ITEM_COLUMNS as Column<GridTypeColumnMap[T]>[];
    case 'TemplateList':
    case 'TemplateListItem':
      return TEMPLATE_COLUMNS as Column<GridTypeColumnMap[T]>[];
    default:
      throw new Error('Unknown grid type');
  }
}

type Column<T extends string = string> = {
  name: T;
  width: number | 'auto';
  canBeHidden: boolean;
  constantWidth?: boolean;
};

type DocumentColumns =
  | 'select'
  | 'name'
  | 'status'
  | 'version'
  | 'date'
  | 'sign';

const DOCUMENT_ITEM_COLUMNS: Column<DocumentColumns>[] = [
  { name: 'name', width: 'auto', canBeHidden: false },
  { name: 'status', width: 152, canBeHidden: true },
  { name: 'version', width: 152, canBeHidden: true },
  { name: 'date', width: 152, canBeHidden: true },
  { name: 'sign', width: 264, canBeHidden: true },
];

const DOCUMENT_LIST_COLUMNS: Column<DocumentColumns>[] = [
  { name: 'select', width: 28, canBeHidden: false, constantWidth: true },
  ...DOCUMENT_ITEM_COLUMNS,
];

type TemplateColumns = 'name' | 'activity';

const TEMPLATE_COLUMNS: Column<TemplateColumns>[] = [
  { name: 'name', width: 'auto', canBeHidden: false },
  { name: 'activity', width: 560, canBeHidden: true },
];

export function useGridTemplate<T extends GridType>(
  tableId: TableId,
  gridType: T,
) {
  const [hiddenColumnNames] = useCustomTableView(tableId);
  const columns = getGridColumns(gridType);

  const grid = useMemo(
    () => buildGridTemplate(columns, hiddenColumnNames),
    [columns, hiddenColumnNames],
  );

  const hideableColumns = useMemo(
    () => columns.filter((col) => col.canBeHidden).map((col) => col.name),
    [columns],
  );

  return {
    grid,
    hiddenColumns: hiddenColumnNames as GridTypeColumnMap[T][],
    hideableColumns,
  };
}

function buildGridTemplate(
  columns: Column[],
  hiddenColumnNames: string[],
): string {
  function getColumnWidth(col: Column): string {
    if (hiddenColumnNames.includes(col.name)) return '0px';
    if (col.width === 'auto') return 'auto';
    if (col.constantWidth) return `${col.width}px`;

    return `${col.width + extraSpacePerColumn}px`;
  }

  const allColumnsCount = columns.length;
  const constantWidthCount = columns.filter((col) => col.constantWidth).length;
  const hiddenColumnWidthSum = columns
    .filter(
      (col) => col.width !== 'auto' && hiddenColumnNames.includes(col.name),
    )
    .reduce((sum, col) => sum + (col.width as number), 0);

  const extraSpacePerColumn =
    hiddenColumnNames.length > 0
      ? hiddenColumnWidthSum /
        (allColumnsCount - hiddenColumnNames.length - constantWidthCount)
      : 0;

  return columns.map(getColumnWidth).join(' ');
}
